import { SubscriptionGate } from "../components/SubscriptionGate";
import { CompareCyclesCard } from "../components/insights/CompareCyclesCard";
import { CycleChartCard } from "../components/insights/CycleChartCard";
import { describeWindowOpening, describeWindowRule } from "../components/insights/fertileWindowCopy";
import {
  Button,
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipTrigger as UiTooltipTrigger,
} from "@nfp-tracker/components";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nfp-tracker/components";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@nfp-tracker/components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@nfp-tracker/components";
import { Input } from "@nfp-tracker/components";
import { Label } from "@nfp-tracker/components";
import { Skeleton } from "@nfp-tracker/components";
import { useToast } from "@nfp-tracker/components";
import { ROUTES } from "../constants/routes";
import { useObservationsCache } from "../hooks/useObservationsCache";
import { useOwnerContext } from "../hooks/useOwnerContext";
import { captureException } from "../integrations/analytics/client";
import { invokeEdgeFunction } from "../integrations/supabase/functions";
import { deriveCyclesFromObservations, isRecordedCycle } from "../utils/derivedCycles";
import { fetchAllObservations } from "../utils/observationsCsvIo";
import {
  BLEEDING_INTENSITY_RANK,
  computeCycleStats,
  findLhSurgeDay,
  getCurrentCycle,
  getDateKeyInTimeZone,
  isPastCycle,
  isUpcomingCycle,
  generatePDF,
  generatePDFBase64,
  computeFertileWindow,
  type NFPMethod,
  usesSymptoThermal,
} from "@nfp-tracker/utils";
import { handleExternalError } from "../utils/errorSnackbar";
import {
  Activity,
  Calendar,
  FileText,
  Info,
  Mail,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [instructorEmail, setInstructorEmail] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Shared, session-cached auth/owner/permission context — repeat navigation between pages
  // no longer re-runs the auth+onboarding+owner-resolution chain from scratch each time.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const coupleId = couple?.id;
  const isPostpartum = ownerContext?.status === "ready" ? ownerContext.isPostpartum : false;
  // Reused as the Insights tab of an instructor's client chart, where entry CTAs would navigate
  // the instructor to their *own* Observations page and out of the client's chart entirely.
  const isClientView =
    ownerContext?.status === "ready" && Boolean(ownerContext.viewingClientUserId);
  const nfpMethods = couple?.methods?.length ? couple.methods : ["Marquette"];
  const userName = couple ? `${couple.first_name || ""} ${couple.last_name || ""}`.trim() : "";

  // Shared cache (~13 months / ~12 cycles), kept fresh by the single realtime subscription
  // mounted in AppShellLayout — no per-page fetch or realtime subscription needed here.
  const { cycles, observations, isLoading: loadingObservations } =
    useObservationsCache(coupleId, nfpMethods);
  const loading = ownerContextQuery.isLoading || loadingObservations;

  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth);
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding);
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    if (ownerContextQuery.error) {
      console.error("Error loading dashboard:", ownerContextQuery.error);
      captureException(ownerContextQuery.error, { source: "dashboard_owner_context" });
    }
  }, [ownerContextQuery.error]);

  // Owner's today. Entries can be dated ahead (pre-logged / seeded), so "current" means the
  // cycle containing today and "completed" means ended before today — not end_date alone.
  const todayKey = getDateKeyInTimeZone(new Date(), couple?.timezone);

  // Calculate cycle lengths — partial cycles (start never recorded) have no measurable length,
  // and a cycle that hasn't finished by today isn't measured yet.
  const cycleLengthData = cycles
    .filter((c) => isRecordedCycle(c) && isPastCycle(c, todayKey))
    .map((c) => {
      // parseISO reads yyyy-MM-dd as a local date; new Date() would read it as UTC midnight
      // and label the previous day west of UTC.
      const start = parseISO(c.start_date);
      const length = differenceInCalendarDays(parseISO(c.end_date!), start) + 1;
      return {
        date: format(start, "MMM yy"),
        length,
        // Null, not 0, for a cycle with no identified Peak — the peak line should break
        // over that cycle rather than dive to the axis.
        peak: c.first_peak_cd ?? null,
      };
    })
    .reverse()
    .slice(-12); // Last 12 cycles

  // Statistics — length, peak day, fertile window and post-peak phase all derived from the
  // same cycle set in one place (see computeCycleStats).
  const completedCycles = cycles.filter((c) => isRecordedCycle(c) && isPastCycle(c, todayKey));
  const stats = computeCycleStats(cycles, observations, {
    methods: nfpMethods as NFPMethod[],
    isPostpartum,
    today: todayKey,
  });
  const avgLength = stats.avgLength ?? 0;
  const shortestCycle = stats.shortestLength ?? 0;
  const longestCycle = stats.longestLength ?? 0;

  // Bleeding intensity over time
  const bleedingData = observations
    .filter((o) => o.bleeding !== null)
    .map((o) => ({
      date: format(parseISO(o.date), "MMM d"),
      // Rank, not raw code: "Very Light" (4) and "Brown" (5) aren't more intense than "Heavy" (3).
      intensity: BLEEDING_INTENSITY_RANK[o.bleeding as number] ?? o.bleeding,
    }))
    .slice(-30); // Last 30 days with bleeding

  // Current cycle, for the overview; the cycle chart below picks its own cycle.
  const currentCycle = getCurrentCycle(cycles, todayKey);
  const currentCycleObservations = currentCycle
    ? observations
        .filter((obs) => obs.cycle_id === currentCycle.id)
        .sort((a, b) => a.cycle_day - b.cycle_day)
    : [];
  const lhSurgeCd = findLhSurgeDay(currentCycleObservations);
  // Same close rule as Calendar/CycleHistory; null while the method still needs confirmation
  // (e.g. Sympto-Thermal awaiting the shift, or no mucus change recorded after Peak yet).
  const currentWindowEndCd = currentCycle?.first_peak_cd
    ? computeFertileWindow(currentCycleObservations, nfpMethods as NFPMethod[], {
        peakCd: currentCycle.first_peak_cd,
        isPostpartum,
      }).endCd
    : null;

  const handleExportPDF = async () => {
    try {
      if (!coupleId) return;

      // Full history, paged — a single select caps at 1000 rows (~2.7 years) and would
      // silently drop the oldest cycles from the report.
      const allObservations = await fetchAllObservations(coupleId);
      const { cycles: allCycles, observations: normalizedObs } =
        deriveCyclesFromObservations(allObservations, nfpMethods);

      generatePDF(normalizedObs || [], allCycles || [], userName, nfpMethods, couple?.timezone);

      toast({
        title: "Export Complete",
        description: isClientView
          ? "The client's PDF report has been generated."
          : "Your PDF report has been generated.",
      });
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        title: "Export error",
        logPrefix: "Error generating PDF export:",
      });
    }
  };

  const handleSendToInstructor = async () => {
    try {
      if (!coupleId || !instructorEmail || isClientView) return;

      setSendingEmail(true);

      // Full history, paged — a single select caps at 1000 rows (~2.7 years) and would
      // silently drop the oldest cycles from the report.
      const allObservations = await fetchAllObservations(coupleId);
      const { cycles: allCycles, observations: normalizedObs } =
        deriveCyclesFromObservations(allObservations, nfpMethods);

      const pdfBase64 = generatePDFBase64(
        normalizedObs || [],
        allCycles || [],
        userName,
        nfpMethods,
        couple?.timezone
      );

      const { data, error } = await invokeEdgeFunction(
        "send-chart-email",
        {
          body: {
            instructorEmail: instructorEmail.trim(),
            instructorName: instructorName.trim() || undefined,
            userName,
            pdfBase64,
          },
        }
      );

      if (error) throw error;

      // Instructor registration/linking is coming soon; no persistence in this phase.

      toast({
        title: "Chart Sent!",
        description: `Your fertility chart has been sent to ${instructorEmail}`,
      });

      setShowEmailDialog(false);
      setInstructorEmail("");
      setInstructorName("");
    } catch (error: unknown) {
      handleExternalError(toast, error, {
        title: "Send error",
        logPrefix: "Error sending chart email:",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="relative space-y-6">
        {/* Quick visual cycle overview */}
        <div className="mx-auto mb-8 max-w-3xl">
          <Card className="p-6 flex flex-col items-center justify-center animate-fade-in">
            <CardTitle className="text-lg font-semibold mb-2 text-center">Current Cycle Overview</CardTitle>
            {currentCycle && !isRecordedCycle(currentCycle) ? (
              // Partial cycle: day numbers are offsets from the first logged day, not cycle
              // days, so no "CD n" / start date / Peak CD is claimed for it.
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="text-lg font-semibold">Cycle start not recorded</div>
                <div className="max-w-sm text-sm text-muted-foreground">
                  {isClientView
                    ? "Cycle days appear once this client marks the first day of a cycle."
                    : "Mark the first day of your next period as a cycle start to see cycle days here."}
                </div>
              </div>
            ) : currentCycle ? (
              <>
                <div className="flex flex-col items-center gap-2">
                  <div className="text-4xl font-bold text-primary">
                    CD {differenceInCalendarDays(parseISO(todayKey), parseISO(currentCycle.start_date)) + 1}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Started: {format(parseISO(currentCycle.start_date), "PP")}
                  </div>
                  {currentCycle.first_peak_cd && (
                    <div className="text-xs text-accent mt-1 text-center">
                      First Peak: Day {currentCycle.first_peak_cd}
                      <br />
                      {currentWindowEndCd !== null
                        ? `Fertile window through Day ${currentWindowEndCd}`
                        : usesSymptoThermal(nfpMethods)
                          ? "Fertile window open — waiting for the temperature shift and mucus change to confirm"
                          : "Fertile window open — waiting for a mucus change after Peak to confirm"}
                    </div>
                  )}
                  {lhSurgeCd !== null && !currentCycle.first_peak_cd && (
                    <div className="text-xs text-muted-foreground mt-1 text-center">
                      Positive ovulation test on CD {lhSurgeCd} — Peak Day not yet identified by your method
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground text-center">
                {isClientView
                  ? "No active cycle. This client has not started tracking yet."
                  : "No active cycle. Start tracking to see your cycle overview!"}
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid gap-4 md:grid-cols-3">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      coupleId={coupleId}
      readOnlyMessage="Subscribe to access your cycle summaries and analytics."
    >
      <div className="space-y-6 pb-8">
          <div className="flex justify-end">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              {/* Sharing is the client's call: an instructor viewing a client's chart can export
                  what they can already see, but not email the client's full chart elsewhere. */}
              {!isClientView && (
              <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
                <DialogTrigger asChild>
                  <Button variant="default" size="sm">
                    <Mail className="h-4 w-4 mr-2" />
                    Send to Instructor
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Send Chart to Instructor</DialogTitle>
                    <DialogDescription>
                      Share your fertility tracking chart with your NFP
                      instructor via email.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="instructor-email">
                        Instructor Email *
                      </Label>
                      <Input
                        id="instructor-email"
                        type="email"
                        placeholder="instructor@example.com"
                        value={instructorEmail}
                        onChange={(e) => setInstructorEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instructor-name">
                        Instructor Name (optional)
                      </Label>
                      <Input
                        id="instructor-name"
                        type="text"
                        placeholder="e.g., Sarah Johnson"
                        value={instructorName}
                        onChange={(e) => setInstructorName(e.target.value)}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      onClick={handleSendToInstructor}
                      disabled={!instructorEmail || sendingEmail}
                    >
                      {sendingEmail ? "Sending..." : "Send Chart"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="animate-fade-in [animation-delay:0ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Average Cycle
                </CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{avgLength} days</div>
                <p className="text-xs text-muted-foreground">
                  {completedCycles.length} completed cycles
                  {isPostpartum && " (postpartum tracking)"}
                </p>
              </CardContent>
            </Card>

            <Card className="animate-fade-in [animation-delay:100ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Shortest Cycle
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{shortestCycle} days</div>
                <p className="text-xs text-muted-foreground">
                  Minimum observed
                </p>
              </CardContent>
            </Card>

            <Card className="animate-fade-in [animation-delay:200ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Longest Cycle
                </CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{longestCycle} days</div>
                <p className="text-xs text-muted-foreground">
                  Maximum observed
                </p>
              </CardContent>
            </Card>

            <Card className="animate-fade-in [animation-delay:300ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Average Peak Day
                </CardTitle>
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.avgPeakDay ? `CD ${stats.avgPeakDay}` : "—"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.avgPeakDay
                    ? `Range CD ${stats.earliestPeakDay}–${stats.latestPeakDay} · ovulation ≈ CD ${stats.estimatedOvulationCd}`
                    : "No Peak Day identified yet"}
                </p>
              </CardContent>
            </Card>

            <Card className="animate-fade-in [animation-delay:400ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Fertile Window
                </CardTitle>
                <UiTooltip>
                  <UiTooltipTrigger asChild>
                    <button type="button" aria-label="About the fertile window">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </UiTooltipTrigger>
                  <UiTooltipContent side="left" className="max-w-64 space-y-1">
                    {stats.fertileWindowStartBasis ? (
                      <span className="block">{describeWindowRule(stats.fertileWindowStartBasis)}</span>
                    ) : null}
                    <span className="block">
                    Averaged from each cycle's own window under your
                    {nfpMethods.length > 1 ? " methods" : ` ${nfpMethods[0]}`}{" "}
                    rules, closing 3 full days after Peak Day. Sperm survival and
                    ovum viability alone imply roughly CD{" "}
                    {stats.biologicalWindowStartCd}–{stats.biologicalWindowEndCd}.
                    </span>
                  </UiTooltipContent>
                </UiTooltip>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.fertileWindowLength
                    ? `CD ${stats.fertileWindowStartCd}–${stats.fertileWindowEndCd}`
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.fertileWindowLength && stats.fertileWindowStartBasis
                    ? `~${stats.fertileWindowLength} days · ${describeWindowOpening(
                        stats.fertileWindowStartBasis,
                        stats.fertileWindowStartCd
                      )}`
                    : "Needs a cycle with a Peak Day"}
                </p>
              </CardContent>
            </Card>

            <Card className="animate-fade-in [animation-delay:500ms]">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Post-Peak Phase
                </CardTitle>
                <UiTooltip>
                  <UiTooltipTrigger asChild>
                    <button type="button" aria-label="About the post-peak phase">
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </UiTooltipTrigger>
                  <UiTooltipContent side="left" className="max-w-56">
                    Days from your Peak Day to the end of the cycle. Peak Day
                    approximates ovulation, so this estimates the luteal phase
                    rather than measuring it.
                  </UiTooltipContent>
                </UiTooltip>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.avgPostPeakLength
                    ? `${stats.avgPostPeakLength} days`
                    : "—"}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.avgPostPeakLength
                    ? `Range ${stats.shortestPostPeakLength}–${stats.longestPostPeakLength} days · Peak to cycle end`
                    : "Needs a completed cycle with a Peak Day"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Comprehensive cycle chart: temps with every sign lined up by cycle day */}
          {cycles.length > 0 && (
            <CycleChartCard
              className="animate-fade-in [animation-delay:600ms]"
              cycles={cycles}
              observations={observations}
              methods={nfpMethods}
              isPostpartum={isPostpartum}
              optionalFields={couple?.optional_fields}
              today={todayKey}
            />
          )}

          {/* Temperatures of recent cycles overlaid (by CD 1, Peak Day or temperature shift) */}
          {cycles.filter((c) => !isUpcomingCycle(c, todayKey)).length > 1 && (
            <CompareCyclesCard
              className="animate-fade-in [animation-delay:650ms]"
              cycles={cycles}
              observations={observations}
              today={todayKey}
            />
          )}

          {/* Cycle Length Chart */}
          {cycleLengthData.length > 0 && (
            <Card className="animate-fade-in [animation-delay:600ms]">
              <CardHeader>
                <CardTitle>Cycle Length History</CardTitle>
                <CardDescription>
                  Cycle length with the Peak Day of each cycle, over the past year
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    length: {
                      label: "Cycle Length",
                      color: "hsl(var(--primary))",
                    },
                    peak: {
                      label: "Peak Day",
                      color: "hsl(var(--accent))",
                    },
                  }}
                  className="h-[300px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={cycleLengthData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar
                        dataKey="length"
                        fill="hsl(var(--primary))"
                        radius={[8, 8, 0, 0]}
                      />
                      <Line
                        type="monotone"
                        dataKey="peak"
                        stroke="hsl(var(--accent))"
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--accent))", r: 3 }}
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Bleeding Pattern Chart */}
          {bleedingData.length > 0 && (
            <Card className="animate-fade-in [animation-delay:700ms]">
              <CardHeader>
                <CardTitle>Bleeding Intensity Pattern</CardTitle>
                <CardDescription>Recent bleeding observations</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer
                  config={{
                    intensity: {
                      label: "Intensity",
                      color: "hsl(var(--destructive))",
                    },
                  }}
                  className="h-[250px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={bleedingData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        domain={[0, 4]}
                      />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line
                        type="monotone"
                        dataKey="intensity"
                        stroke="hsl(var(--destructive))"
                        strokeWidth={2}
                        dot={{ fill: "hsl(var(--destructive))", r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {cycles.length === 0 && cycleLengthData.length === 0 && bleedingData.length === 0 && (
            <Card className="animate-fade-in">
              <CardContent className="flex flex-col items-center justify-center py-12 px-6">
                <div className="rounded-full bg-muted p-6 mb-4">
                  <Activity className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No Insights Yet</h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  {isClientView
                    ? "Insights appear once this client has logged daily observations."
                    : "Start tracking your daily observations to see beautiful summaries, patterns, and trends in your logged cycle data."}
                </p>
                {!isClientView && (
                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                    <Button
                      onClick={() => navigate(ROUTES.observations)}
                      variant="default"
                      className="w-full sm:w-auto"
                    >
                      Start Tracking Observations
                    </Button>
                    <Button
                      onClick={() => navigate(ROUTES.cycleHistory)}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      View Cycle History
                    </Button>
                  </div>
                )}
                <div className="mt-8 p-4 bg-muted rounded-lg max-w-md">
                  <h4 className="font-semibold text-sm mb-2">
                    What you'll discover:
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Average cycle length and patterns</li>
                    <li>• Recorded cycle markers over time</li>
                    <li>• Bleeding intensity trends</li>
                    <li>• Cycle summaries based on logged data</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        {/* Floating plus icon for quick logging */}
        {!isClientView && (
        <UiTooltip>
          <UiTooltipTrigger asChild>
            <button
              aria-label="Log new entry"
              className="safe-fixed-bottom fixed right-[calc(env(safe-area-inset-right,0px)+1.5rem)] w-14 h-14 rounded-full bg-primary shadow-lg flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary/50 z-50"
              onClick={() =>
                navigate(`${ROUTES.observations}?date=${new Date().toISOString().split("T")[0]}`)
              }
            >
              <span className="sr-only">Log new entry</span>
              <svg width="28" height="28" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
          </UiTooltipTrigger>
          <UiTooltipContent side="left">Log entry</UiTooltipContent>
        </UiTooltip>
        )}
    </SubscriptionGate>
  );
};

export default Dashboard;
